import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Archive, BellOff, ChevronRight, GripVertical, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { SystemNotification } from "@nh/shared";

export interface NotificationsPanelBodyProps {
  items: SystemNotification[];
  /** Sale de la campana pero NO se borra. */
  onArchive?: (id: string) => void;
  /** Se va de verdad. */
  onRemove?: (id: string) => void;
}

/**
 * ═══════════════════════════════════════════════════════════════════
 * La lista de la campana, y sus ACCIONES (Anahí, 29-ago).
 *
 * Pasa de ser algo que solo se lee a algo que se ATIENDE: cada aviso se
 * puede archivar —quítamelo de enfrente, pero consérvalo— o eliminar.
 * El asa lo desliza y descubre los tres botones debajo; la palanca
 * vuelve a cerrarlo.
 *
 * ── Por qué no es el componente de shadcn que circula ────────────────
 * Aquel trae `components/ui`, `@radix-ui/react-popover`, `Card`,
 * `Badge` y los tokens `bg-muted`/`text-muted-foreground`. Este
 * proyecto tiene su design system (`ds-*`) y su propio popover —el que
 * ya usa `UserMenu`, con su foco y su Escape resueltos—. Copiarlo
 * entero metería un segundo sistema de estilos y un segundo popover
 * para pintar tres botones.
 *
 * Se conserva su ANATOMÍA —el asa, el deslizamiento de 40 px, archivar
 * · eliminar · cerrar— y se descarta su plomería. La animación se apaga
 * sola si el sistema pide menos movimiento.
 *
 * ── El punto violeta ─────────────────────────────────────────────────
 * Marca lo que estaba SIN leer al abrir. Abrir la campana lo lee todo,
 * pero el punto no se apaga en vivo: se apaga a la siguiente apertura.
 * Así se alcanza a ver QUÉ era lo nuevo en lugar de un parpadeo.
 *
 * ── Por qué el vacío no es un hueco ──────────────────────────────────
 * Sin nada, el panel dice que no hay novedades y por qué volvería a
 * haberlas. Un panel que se abre en blanco es indistinguible de uno
 * roto, y lo que se piensa es "no cargó", no "no hay nada".
 * ═══════════════════════════════════════════════════════════════════
 */
export function NotificationsPanelBody({
  items,
  onArchive,
  onRemove,
}: NotificationsPanelBodyProps) {
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  /** Cuál tiene los botones a la vista. Solo una a la vez. */
  const [abierta, setAbierta] = useState<string | null>(null);

  if (items.length > 0) {
    return (
      <ul className="relative mt-2 grid max-h-[19rem] gap-1 overflow-y-auto">
        {items.map((item) => {
          const activa = abierta === item.id;
          return (
            <li
              key={item.id}
              /*
                `overflow-hidden` en la FILA: al deslizarse el texto 40 px
                se recorta contra el borde de su renglón en vez de
                derramarse sobre lo de al lado, que es lo que se veía.
              */
              className="flex items-center gap-1 overflow-hidden rounded-xl px-2.5 py-2 transition hover:bg-ds-mist/40"
            >
              {/*
                ── Se mueven los BOTONES, no el texto ──────────────────
                El diseño de referencia desplaza el texto 40 px a la
                izquierda para hacerles sitio. Con títulos de una palabra
                ("Welcome") no se nota; con "Tu documentación fue
                aprobada" el aviso pierde sus primeras letras y queda
                ilegible — que es exactamente lo que se veía.
                
                Mismo gesto y misma anatomía, al revés: los botones
                entran deslizándose desde la derecha y el texto cede
                ancho por flex. Nada se corta.
              */}
              <div className="min-w-0 flex-1">
                {/*
                  Título y FECHA en la misma fila, como el diseño de
                  referencia: la fecha ocupaba un renglón entero para
                  decir "29 ago". Así cada aviso mide dos líneas menos y
                  caben tres sin hacer scroll.
                */}
                <div className="flex items-baseline justify-between gap-2">
                  <p className="flex min-w-0 items-start gap-1.5 text-[12.5px] font-semibold leading-snug text-ds-ink">
                    {!item.readAt && (
                      <span
                        aria-label={t("Sin leer")}
                        className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-ds-violet-pop"
                      />
                    )}
                    {item.title}
                  </p>
                  <span className="shrink-0 text-[11px] text-ds-ink-3">
                    {new Date(item.createdAt).toLocaleDateString("es-MX", {
                      day: "numeric",
                      month: "short",
                    })}
                  </span>
                </div>
                {item.body && (
                  <p className="mt-0.5 line-clamp-2 text-[12px] leading-relaxed text-ds-ink-3">
                    {item.body}
                  </p>
                )}
              </div>

              <div className="flex shrink-0 items-center">
                {activa ? (
                  <motion.div
                    initial={reduce ? false : { x: 24, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.2 }}
                    className="flex items-center gap-0.5"
                  >
                    <button
                      type="button"
                      aria-label={t("Archivar")}
                      title={t("Archivar")}
                      className="rounded-lg p-1.5 text-ds-ink-3 transition hover:bg-ds-mist hover:text-ds-ink"
                      onClick={() => {
                        onArchive?.(item.id);
                        setAbierta(null);
                      }}
                    >
                      <Archive className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      aria-label={t("Eliminar")}
                      title={t("Eliminar")}
                      className="rounded-lg p-1.5 text-ds-danger transition hover:bg-ds-mist"
                      onClick={() => {
                        onRemove?.(item.id);
                        setAbierta(null);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      aria-label={t("Cerrar acciones")}
                      className="rounded-lg p-1.5 text-ds-ink-3 transition hover:bg-ds-mist hover:text-ds-ink"
                      onClick={() => setAbierta(null)}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </motion.div>
                ) : (
                  <button
                    type="button"
                    aria-label={t("Acciones de esta notificación")}
                    className="rounded-lg p-1.5 text-ds-ink-3 transition hover:bg-ds-mist hover:text-ds-ink"
                    onClick={() => setAbierta(item.id)}
                  >
                    <GripVertical className="h-4 w-4" />
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <div className="relative mt-2 flex flex-col items-center gap-2 px-2 py-4 text-center">
      <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-ds-mist/70">
        <BellOff aria-hidden="true" className="h-4 w-4 text-ds-purple" />
      </span>
      <p className="text-[13px] font-semibold leading-tight text-ds-ink">
        {t("No hay novedades")}
      </p>
      <p className="text-[12px] leading-relaxed text-ds-ink-3">
        {t("Cuando pase algo en tu cuenta, te enteras aquí.")}
      </p>
    </div>
  );
}
